import React, { createContext, useContext, useState, useEffect } from 'react';
import onTerminalRoute from '../utils/onTerminalRoute';
import useAuth from '../hooks/useAuth';
import API_BASE from '../config';

/**
 * ChartColorContext — TP/SL overlay colors on charts.
 * Default: traffic-light “ready” (amber + brown), outcome-neutral.
 * Green/Red: outcome-indicative (TP vs SL).
 */
const ChartColorContext = createContext();

// Color scheme definitions (keys persisted in localStorage / API)
export const COLOR_SCHEMES = {
  DEFAULT: {
    name: 'Default',
    description:
      'Neutral amber, no outcome implied. Results shown without directional color bias.',
    colors: {
      tp: '#f59e0b', // amber-500 — “ready” / highlight
      sl: '#78350f', // amber-900 — paired earth tone (not a loss implication)
      upBorder: '#f59e0b',
      downBorder: '#78350f',
      upFill: '#fef3c7',
      downFill: '#fde68a66',
    },
  },
  TRADITIONAL: {
    name: 'Green / Red',
    description:
      'Green for TP hit, Red for SL hit. Shows outcome-indicative colors based on your TP/SL selection.',
    colors: {
      tp: '#22c55e',
      sl: '#ef4444',
      upBorder: '#22c55e',
      downBorder: '#ef4444',
      upFill: '#dcfce7',
      downFill: '#fee2e2',
    },
  },
};

export const COLOR_SCHEME_KEYS = Object.keys(COLOR_SCHEMES);

const DEFAULT_SCHEME = 'DEFAULT';
const STORAGE_KEY_COLOR_SCHEME = 'user_chart_color_scheme';

/** Map removed / legacy keys to current schemes */
export const normalizeColorSchemeKey = (key) => {
  if (key && COLOR_SCHEMES[key]) return key;
  if (key === 'SEBI' || key === 'GRAY') return 'DEFAULT';
  return DEFAULT_SCHEME;
};

export const ChartColorProvider = ({ children }) => {
  const { userId, token } = useAuth();
  const [colorScheme, setColorScheme] = useState(DEFAULT_SCHEME);
  const [isInitialized, setIsInitialized] = useState(false);

  const [isDarkMode, setIsDarkMode] = useState(() =>
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const updateDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };

    const observer = new MutationObserver(updateDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    const handleStorageChange = (e) => {
      if (e.key === 'theme') {
        updateDarkMode();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      observer.disconnect();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const getCurrentScheme = () => {
    const key = normalizeColorSchemeKey(colorScheme);
    return COLOR_SCHEMES[key] || COLOR_SCHEMES[DEFAULT_SCHEME];
  };

  useEffect(() => {
    const loadUserPreference = async () => {
      try {
        const storedScheme = localStorage.getItem(STORAGE_KEY_COLOR_SCHEME);
        if (storedScheme) {
          const normalized = normalizeColorSchemeKey(storedScheme);
          setColorScheme(normalized);
          if (normalized !== storedScheme) {
            localStorage.setItem(STORAGE_KEY_COLOR_SCHEME, normalized);
          }
        }

        // Terminal draws its own chart colours from the token layer and never
        // reads this context — the remote preference is dead weight there.
        if (userId && token && !onTerminalRoute()) {
          try {
            const response = await fetch(`${API_BASE}/user/chart-color-preference`, {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.data?.color_scheme) {
                const apiScheme = normalizeColorSchemeKey(data.data.color_scheme);
                if (COLOR_SCHEMES[apiScheme]) {
                  setColorScheme(apiScheme);
                  localStorage.setItem(STORAGE_KEY_COLOR_SCHEME, apiScheme);
                }
              }
            }
          } catch (apiError) {
            console.log('Chart color preference API not available, using localStorage');
          }
        }
      } catch (error) {
        console.error('Error loading chart color preference:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    loadUserPreference();
  }, [userId, token]);

  const saveColorScheme = async (scheme) => {
    const key = normalizeColorSchemeKey(scheme);
    if (!COLOR_SCHEMES[key]) {
      console.error('Invalid color scheme:', scheme);
      return;
    }

    try {
      localStorage.setItem(STORAGE_KEY_COLOR_SCHEME, key);
      setColorScheme(key);

      if (userId && token) {
        try {
          const response = await fetch(`${API_BASE}/user/chart-color-preference`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              color_scheme: key,
            }),
          });

          if (!response.ok) {
            console.error('Failed to save color scheme to server:', await response.text());
          }
        } catch (apiError) {
          console.log('Chart color preference API not available, saved to localStorage only');
        }
      }
    } catch (error) {
      console.error('Error saving color scheme:', error);
    }
  };

  const changeColorScheme = async (scheme) => {
    await saveColorScheme(scheme);
  };

  const getColors = () => {
    return getCurrentScheme().colors;
  };

  const currentScheme = getCurrentScheme();

  const value = {
    colorScheme: normalizeColorSchemeKey(colorScheme),
    currentScheme,
    colors: currentScheme.colors,
    isInitialized,
    changeColorScheme,
    getColors,
    COLOR_SCHEMES,
    isDarkMode,
  };

  return (
    <ChartColorContext.Provider value={value}>
      {children}
    </ChartColorContext.Provider>
  );
};

export const useChartColor = () => {
  const context = useContext(ChartColorContext);
  if (!context) {
    throw new Error('useChartColor must be used within a ChartColorProvider');
  }
  return context;
};

export default ChartColorContext;
